import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import useTitle from '../../../systems/TitleManager/useTitle';
import { getRubric, updateRubric } from '../../../apiClients/backendApi';
import createSnackbarAction from '../../../utils/createSnackbarAction';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import TaskTypes from '@dyl/task-types';
import { useLevels } from '@dyl/systems/LevelsManager';
import Skills from '@dyl/constants/Skills';
import createRubric from "@dyl/apiClients/backendApi/createRubric";
import { useRubrics } from "@dyl/systems/RubricManager";

const PHASE_NULL_TEXT = 'N/A';

export default function useLogic() {
    const { globalLevel } = useLevels();
    const { refreshRubrics } = useRubrics();
    const { taskType, rubricId } = useParams();
    const isNew = !rubricId;
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const { skill, label: taskTypeName, rubric } = useMemo(() => {
        return TaskTypes[globalLevel.label].find(t => t.id === taskType);
    }, [taskType]) ?? {};

    const skillName = useMemo(() => Skills.find(s => s.value === skill)?.label ?? '???', [skill]);

    const phaseCount = rubric?.phaseCount ?? 1;
    const phases = phaseCount === 1
        ? [PHASE_NULL_TEXT]
        : Array(phaseCount).fill().map((_, x) => `PHASE_${x+1}`);
    const [phase, setPhase] = useState(phases[0]);

    const [text, setText] = useState('');
    const [audioId, setAudioId]  = useState('');

    const [isLoading, setLoading] = useState(true);


    const title = (() => {
        if (text) {
            return text;
        }
        else if (isNew) {
            return `New Rubric`;
        }
        else {
            return 'Loading...';
        }
    })();

    useTitle({ breadcrumbs: ['Rubrics', taskTypeName, title] });


    useEffect(() => {
        (async () => {
            if (!isNew) {
                try {
                    const {
                        rubric_text,
                        rubric_audio_id,
                        phase,
                    } = await getRubric(rubricId);

                    setText(rubric_text);
                    setAudioId(rubric_audio_id);
                    setPhase(phase ?? PHASE_NULL_TEXT);
                }
                catch (error) {
                    console.error(error);
                    await new Promise(resolve => {
                        enqueueSnackbar('Failed to fetch rubric', {
                            variant: 'error',
                            action: createSnackbarAction({
                                iconComponent: RefreshIcon,
                                onClick: resolve,
                            }),
                            persist: true
                        });
                    });
                }
            }
            setLoading(false);
        })();
    }, []);

    const [isSaving, setSaving] = useState(false);

    const submit = async () => {
        setSaving(true);

        const rubric = {
            rubric_id: rubricId,
            skill_id: skill,
            task_type_id: taskType,
            level_id: globalLevel.level_id,

            phase: !!phase && phase !== PHASE_NULL_TEXT ? phase : null,
            rubric_text: text || null,
            rubric_audio_id: audioId || null,
        };

        try {
            if (isNew) {
                const { rubric_id } = await createRubric(rubric);
                enqueueSnackbar('Rubric created', { variant: 'success' });
                navigate(`/${globalLevel.label}/rubrics/${taskType}/${rubric_id}`, {
                    state: { ignoreExitPrompt: true },
                });
            } else {
                await updateRubric(rubric);
                enqueueSnackbar('Rubric updated', { variant: 'success' });
            }
            refreshRubrics();
        }
        catch (error) {
            const { code } = error?.response?.data;

            if (code === 'ERR_280') {
                enqueueSnackbar('You can’t update this rubric as it is associated with a task that is in a test not in ‘Draft’ status.', { variant: 'error' });
            } else {
                enqueueSnackbar('Failed to update rubric.', { variant: 'error' });
            }
        }
        finally {
            setSaving(false);
        }
    };

    return {
        isLoading,
        isSaving,
        submit,

        // TODO: Get level from rubric once they are associated
        levelName: globalLevel?.label ?? '???',
        skillName,
        taskTypeName,
        phase, setPhase, phases,

        text, setText,
        audioId, setAudioId
    };
}